import {
  FileInput,
  FileList,
  SelectGroup,
  Stack
} from '@efast_public/fjd-component-library';
import React, { ChangeEvent, Dispatch, useState } from 'react';
import {
  ActionType,
  FileUploadAction
} from '../../api/models/UploadFiles/FileUploadAction';
import { UploadFiles } from '../../api/models/UploadFiles/UploadFiles';
import { KommunikationAttachmentModel } from '../../modals/NachrichtSenden/KommunikationRequestModel';
import UploadFilePreviewModal from '../../modals/NachrichtSenden/UploadFilePreviewModal';

interface UploadsListProps {
  readonly applicationIsPositiveDecided?: boolean | undefined;
  readonly setApplicationIsPositiveDecided?: (
    isPositiveDecided: boolean | undefined
  ) => void;
  readonly uploadFiles: UploadFiles;
  readonly dispatch: Dispatch<FileUploadAction>;
  readonly hasUploadButtonEnabled: boolean;
  readonly uploadButtonTextZeroFilesUploaded?: string;
  readonly uploadButtonTextAtLeastOneFileUploaded?: string;
  readonly showPreview?: boolean;
  readonly hasBescheidSelect?: boolean;
  readonly hasIconsRightAligned?: boolean;
}

export default function UploadsList(props: UploadsListProps) {
  const [previewPayload, setPreviewPayload] = useState<undefined | string>(
    undefined
  );

  const toPreviewFunction = (uploadedFile: KommunikationAttachmentModel) => {
    if (!props.showPreview) {
      return undefined;
    }

    if (uploadedFile.mimeType === 'application/pdf') {
      return () => {
        setPreviewPayload(uploadedFile.content);
      };
    }

    return undefined;
  };

  const toRemoveFileFunction = (index: number) => {
    return () => {
      props.dispatch({ type: ActionType.REMOVE_ADDITIONAL_FILE, index: index });
    };
  };

  const toRemoveBescheidFunction = () => {
    return () => {
      props.dispatch({ type: ActionType.REMOVE_BESCHEID });
    };
  };

  const truncate = (str: string, n: number): string => {
    return str.length > n ? `${str.slice(0, n - 1)}...` : str;
  };

  const handleClick = (
    event: React.MouseEvent<HTMLInputElement, MouseEvent>
  ): void => {
    if (props.setApplicationIsPositiveDecided) {
      if (event.currentTarget.hasAttribute('checked')) {
        props.setApplicationIsPositiveDecided(undefined);
      } else {
        props.setApplicationIsPositiveDecided(
          event.currentTarget.value === 'positive'
        );
      }
    }
  };

  const { bescheidUpload, additionalFiles } = props.uploadFiles;

  return (
    <Stack direction="column" spacing="l">
      <UploadFilePreviewModal
        payload={previewPayload}
        onClose={() => setPreviewPayload(undefined)}
        isOpen={!!previewPayload}
      />
      {bescheidUpload && (
        <>
          <FileList>
            <FileList.FileItem
              file={{
                name: truncate(
                  bescheidUpload.fileName,
                  props.hasBescheidSelect ? 35 : 55
                )
              }}
              onDelete={toRemoveBescheidFunction()}
              onClick={toPreviewFunction(bescheidUpload)}
            />
          </FileList>
          {props.hasBescheidSelect && (
            <SelectGroup
              id="btn-select-group-upload-list-bescheid"
              name="result"
              selectType="single"
            >
              <SelectGroup.Button
                checked={props.applicationIsPositiveDecided === true}
                value="positive"
                label={"✓ Positiv"}
                onClick={handleClick}
              />
              <SelectGroup.Button
                checked={
                  props.applicationIsPositiveDecided !== undefined &&
                  !props.applicationIsPositiveDecided
                }
                label={"✗ Negativ"}
                value="negative"
                onClick={handleClick}
              />
            </SelectGroup>
          )}
        </>
      )}
      {additionalFiles.length > 0 && (
        <>
          {bescheidUpload && (
            <div
              style={{
                color: '#333333',
                fontWeight: 700,
                fontSize: '0.97rem'
              }}
            >
              Weitere Dokumente
            </div>
          )}
          <FileList>
            {additionalFiles.map(
              (file: KommunikationAttachmentModel, index: number) => (
                <FileList.FileItem
                  key={index}
                  file={{ name: truncate(file.fileName, 55) }}
                  onDelete={toRemoveFileFunction(index)}
                  onClick={toPreviewFunction(file)}
                />
              )
            )}
          </FileList>
        </>
      )}
      {props.hasUploadButtonEnabled && (
        <FileInput
          id="upload_documents_button"
          label={
            bescheidUpload
              ? (props.uploadButtonTextAtLeastOneFileUploaded ?? '')
              : (props.uploadButtonTextZeroFilesUploaded ?? '')
          }
          multiple
          name="upload_more_documents_button"
          onChange={(files) => {
            props.dispatch({
              type: ActionType.ADD_ADDITIONAL_FILES,
              files: files
            });
          }}
        />
      )}
    </Stack>
  );
}
